import { useState } from 'react';
import { showToast } from 'Toast';
import { ERROR_TOAST_MS, messageFrom, type S3Asset } from 'ContentItemEditor';
import { fileTypeForUrl } from './fileTypeForUrl';

import { streamEditorApi } from './streamEditorApi';
import { type StreamSummary, type UploadedResource } from './StreamEditor.types';

export function resourceFromAsset(asset: S3Asset): UploadedResource {
    const url = asset.formats.original.url;

    return {
        id: asset.id,
        url,
        title: asset.file_file_name,
        type: fileTypeForUrl(url),
    };
}

export function summaryFromAsset(asset: S3Asset): StreamSummary {
    // A summary also carries the lessons it covers, and starts with none.
    return { ...resourceFromAsset(asset), lessons: [] };
}

/*
    Uploads files to the shared asset endpoint and hands back the rows to append.

    The Angular page reported a failure in a modal built from err.message, falling back to
    'Something went wrong'. This reports the server's message in a danger toast, because RTK Query
    does not go through the Angular error pipeline and would otherwise show the admin nothing.
*/
export function useStreamAssetUpload() {
    const [uploadAssets] = streamEditorApi.useUploadAssetsMutation();
    const [uploadingFor, setUploadingFor] = useState<string | null>(null);

    async function upload(files: File[], { field }: { field: string }): Promise<S3Asset[]> {
        if (files.length === 0) return [];

        setUploadingFor(field);

        try {
            return await uploadAssets({ files }).unwrap();
        } catch (error) {
            showToast({
                message: 'The upload failed',
                description: messageFrom(error, 'Something went wrong'),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
            return [];
        } finally {
            setUploadingFor(null);
        }
    }

    return { upload, uploadingFor };
}
